/**
 * Mock Trade Intelligence Service & Data Store
 * 
 * Provides mock API responses formatted precisely according to the backend contract.
 * Backend engineers can inspect the return values or copy the schema for their endpoints.
 */

import type {
  ApiResponse,
  MarketOverviewData,
  TopExportProductsData,
  GlobalTradeCorridorsData,
  CategoryDistributionData,
  IndiaHubsData,
  TrendingProductsData,
} from "@/types/tradeIntelligence.types"

// ----------------------------------------------------------------------------
// 1. Market Overview & Live KPIs Mock Response
// ----------------------------------------------------------------------------
export const MOCK_MARKET_OVERVIEW: ApiResponse<MarketOverviewData> = {
  status: "success",
  timestamp: "2026-10-07T10:00:00Z",
  apiVersion: "v1.2",
  data: {
    timeframe: "today",
    kpis: {
      activeRfqs: {
        value: 384,
        displayValue: "384",
        changePercentage: 18.2,
        isPositive: true,
        sparkline: [210, 240, 290, 275, 310, 350, 384],
      },
      verifiedSuppliers: {
        value: 1680,
        displayValue: "1,680+",
        changePercentage: 12.5,
        isPositive: true,
        sparkline: [1420, 1460, 1510, 1560, 1600, 1640, 1680],
      },
      grossTradeVolumeUsd: {
        value: 52400000,
        displayValue: "$52.4M",
        changePercentage: 24.8,
        isPositive: true,
        sparkline: [32, 36, 40, 44, 47, 50, 52.4],
      },
      avgQuotationTurnaroundHours: {
        value: 3.4,
        displayValue: "3.4h",
        slaAdherenceRate: 99.2,
        sparkline: [6.2, 5.5, 4.8, 4.2, 3.9, 3.6, 3.4],
      },
    },
    sectorCapacity: [
      {
        sectorId: "textiles",
        sectorName: "Textiles & Yarns",
        buyerDemandPercent: 94,
        supplierAvailabilityPercent: 88,
        fulfillmentRatePercent: 93.6,
      },
      {
        sectorId: "engineering",
        sectorName: "Precision & Heavy Engineering",
        buyerDemandPercent: 88,
        supplierAvailabilityPercent: 82,
        fulfillmentRatePercent: 91.2,
      },
      {
        sectorId: "chemicals",
        sectorName: "Specialty Chemicals & Polymers",
        buyerDemandPercent: 79,
        supplierAvailabilityPercent: 85,
        fulfillmentRatePercent: 96.0,
      },
      {
        sectorId: "cleantech",
        sectorName: "Solar & Clean Tech",
        buyerDemandPercent: 82,
        supplierAvailabilityPercent: 76,
        fulfillmentRatePercent: 88.5,
      },
      // {
      //   sectorId: "agro",
      //   sectorName: "Agro, Spices & Food Processing",
      //   buyerDemandPercent: 86,
      //   supplierAvailabilityPercent: 91,
      //   fulfillmentRatePercent: 94.4,
      // },
    ],
    liveActivityTicker: [
      {
        id: "act-1",
        text: "Automated RFQ matched: 50,000m Organic Cotton Twill for Hamburg, Germany",
        timestamp: "Just now",
        category: "rfq",
      },
      {
        id: "act-2",
        text: "Surat Cluster: Solar framing modules export batch cleared for Port Jebel Ali (UAE)",
        timestamp: "2m ago",
        category: "shipment",
      },
      {
        id: "act-3",
        text: "New Tier-1 Verified Manufacturer joined: Ludhiana Precision Forgings (ISO 9001)",
        timestamp: "5m ago",
        category: "milestone",
      },
      {
        id: "act-4",
        text: "Demand Surge Alert: +28% increase in Bio-Degradable Jute packaging RFQs from UK buyers",
        timestamp: "9m ago",
        category: "surge",
      },
    ],
  },
  meta: {
    lastUpdated: "2026-10-07T10:00:00Z",
    refreshIntervalSeconds: 30,
  },
}

// ----------------------------------------------------------------------------
// 2. Top Exported Products from India Mock Response
// ----------------------------------------------------------------------------
export const MOCK_TOP_EXPORTS: ApiResponse<TopExportProductsData> = {
  status: "success",
  timestamp: "2026-10-07T10:00:00Z",
  apiVersion: "v1.2",
  data: {
    totalExportVolumeUsdBillions: 437.1,
    fiscalYear: "FY 2025-26",
    products: [
      {
        id: "exp-1",
        name: "Engineering Goods & Castings",
        category: "Engineering",
        exportValueUsdBillions: 109.3,
        shareOfTotalExportsPercent: 25.0,
        yoyGrowthPercent: 11.4,
        topDestinationCountries: ["USA", "Germany", "UAE", "Saudi Arabia"],
        hsnCodePrefix: "84-85",
        demandIndex: 96,
        trend: "high_demand",
      },
      {
        id: "exp-2",
        name: "Petroleum & Refined Products",
        category: "Energy & Petrochem",
        exportValueUsdBillions: 84.6,
        shareOfTotalExportsPercent: 19.3,
        yoyGrowthPercent: 8.2,
        topDestinationCountries: ["Netherlands", "UAE", "Singapore"],
        hsnCodePrefix: "27",
        demandIndex: 91,
        trend: "stable",
      },
      {
        id: "exp-3",
        name: "Gems, Jewelry & Polished Diamonds",
        category: "Gems & Jewelry",
        exportValueUsdBillions: 38.2,
        shareOfTotalExportsPercent: 8.7,
        yoyGrowthPercent: 6.8,
        topDestinationCountries: ["USA", "Hong Kong", "UAE", "Belgium"],
        hsnCodePrefix: "71",
        demandIndex: 88,
        trend: "stable",
      },
      {
        id: "exp-4",
        name: "Textiles, Yarn & Ready-to-Wear",
        category: "Apparel & Textiles",
        exportValueUsdBillions: 36.5,
        shareOfTotalExportsPercent: 8.3,
        yoyGrowthPercent: 14.6,
        topDestinationCountries: ["USA", "UK", "Germany", "UAE"],
        hsnCodePrefix: "52-63",
        demandIndex: 95,
        trend: "rising",
      },
      {
        id: "exp-5",
        name: "Pharmaceuticals & Active Ingredients (API)",
        category: "Healthcare",
        exportValueUsdBillions: 27.9,
        shareOfTotalExportsPercent: 6.4,
        yoyGrowthPercent: 16.8,
        topDestinationCountries: ["USA", "UK", "South Africa", "Brazil"],
        hsnCodePrefix: "30",
        demandIndex: 98,
        trend: "high_demand",
      },
      {
        id: "exp-6",
        name: "Electronics, PV Cells & Hardware",
        category: "Electronics",
        exportValueUsdBillions: 24.1,
        shareOfTotalExportsPercent: 5.5,
        yoyGrowthPercent: 23.5,
        topDestinationCountries: ["USA", "UAE", "Netherlands", "UK"],
        hsnCodePrefix: "85",
        demandIndex: 99,
        trend: "rising",
      },
    ],
  },
}

// ----------------------------------------------------------------------------
// 3. Global Trade Corridors Mock Response
// ----------------------------------------------------------------------------
export const MOCK_TRADE_CORRIDORS: ApiResponse<GlobalTradeCorridorsData> = {
  status: "success",
  timestamp: "2026-10-07T10:00:00Z",
  apiVersion: "v1.2",
  data: {
    originCountry: {
      name: "India",
      code: "IN",
      coordinates: { lat: 20.5937, lng: 78.9629 },
    },
    corridors: [
      {
        id: "corridor-uae",
        countryCode: "AE",
        countryName: "United Arab Emirates",
        flagEmoji: "🇦🇪",
        region: "Middle East",
        bilateralVolumeUsdBillions: 84.5,
        exportGrowthPercent: 16.2,
        averageTransitDaysBySea: 4,
        tradeAgreement: "CEPA Comprehensive Agreement",
        majorExportCommodities: ["Gems & Jewelry", "Petroleum Products", "Engineering Goods"],
        topIndianExitPorts: ["JNPT Mumbai", "Mundra Port"],
        geoCoordinates: { lat: 24.4539, lng: 54.3773 },
        corridorStatus: "high_volume",
      },
      {
        id: "corridor-usa",
        countryCode: "US",
        countryName: "United States",
        flagEmoji: "🇺🇸",
        region: "North America",
        bilateralVolumeUsdBillions: 118.3,
        exportGrowthPercent: 12.8,
        averageTransitDaysBySea: 22,
        tradeAgreement: "Strategic Trade Corridor (US-India)",
        majorExportCommodities: ["Pharma APIs", "Apparel & Cotton", "Electronics & Machinery"],
        topIndianExitPorts: ["JNPT Mumbai", "Chennai Port"],
        geoCoordinates: { lat: 37.0902, lng: -95.7129 },
        corridorStatus: "high_volume",
      },
      {
        id: "corridor-nl",
        countryCode: "NL",
        countryName: "Netherlands & EU Gateway",
        flagEmoji: "🇳🇱",
        region: "Europe",
        bilateralVolumeUsdBillions: 26.8,
        exportGrowthPercent: 21.4,
        averageTransitDaysBySea: 18,
        tradeAgreement: "India-EU Trade Pathway",
        majorExportCommodities: ["Refined Biofuels", "Chemicals", "Organic Agri Produce"],
        topIndianExitPorts: ["Mundra Port", "Cochin Port"],
        geoCoordinates: { lat: 52.1326, lng: 5.2913 },
        corridorStatus: "fastest_growing",
      },
      {
        id: "corridor-sa",
        countryCode: "SA",
        countryName: "Saudi Arabia",
        flagEmoji: "🇸🇦",
        region: "Middle East",
        bilateralVolumeUsdBillions: 43.1,
        exportGrowthPercent: 14.1,
        averageTransitDaysBySea: 6,
        tradeAgreement: "GCC Strategic Partnership",
        majorExportCommodities: ["Basmati Rice & Grains", "Structural Steel", "Ceramic Tiles"],
        topIndianExitPorts: ["Kandla Port", "JNPT Mumbai"],
        geoCoordinates: { lat: 23.8859, lng: 45.0792 },
        corridorStatus: "strategic",
      },
      {
        id: "corridor-de",
        countryCode: "DE",
        countryName: "Germany",
        flagEmoji: "🇩🇪",
        region: "Europe",
        bilateralVolumeUsdBillions: 31.4,
        exportGrowthPercent: 9.7,
        averageTransitDaysBySea: 20,
        tradeAgreement: "EU-India Bilateral Pathway",
        majorExportCommodities: ["Precision Auto Parts", "Textile Machinery", "Specialty Polymers"],
        topIndianExitPorts: ["JNPT Mumbai", "Mundra Port"],
        geoCoordinates: { lat: 51.1657, lng: 10.4515 },
        corridorStatus: "strategic",
      },
      {
        id: "corridor-au",
        countryCode: "AU",
        countryName: "Australia",
        flagEmoji: "🇦🇺",
        region: "Asia-Pacific",
        bilateralVolumeUsdBillions: 24.2,
        exportGrowthPercent: 18.5,
        averageTransitDaysBySea: 16,
        tradeAgreement: "ECTA (Economic Cooperation & Trade)",
        majorExportCommodities: ["Precision Forgings", "Garments", "Solar Mounting Systems"],
        topIndianExitPorts: ["Chennai Port", "Visakhapatnam"],
        geoCoordinates: { lat: -25.2744, lng: 133.7751 },
        corridorStatus: "fastest_growing",
      },
    ],
  },
}

// ----------------------------------------------------------------------------
// 4. Category Trade Distribution Mock Response
// ----------------------------------------------------------------------------
export const MOCK_CATEGORY_DISTRIBUTION: ApiResponse<CategoryDistributionData> = {
  status: "success",
  timestamp: "2026-10-07T10:00:00Z",
  apiVersion: "v1.2",
  data: {
    totalAnalyzedVolumeUsdBillions: 320.5,
    categories: [
      {
        id: "cat-engineering",
        name: "Engineering & Precision Castings",
        shortLabel: "Engineering",
        sharePercent: 34,
        tradeVolumeUsdBillions: 109.3,
        activeSuppliersCount: 540,
        colorHex: "#be001e", // Primary Brand Red
        iconName: "Cpu",
      },
      {
        id: "cat-textiles",
        name: "Textiles, Yarns & Garments",
        shortLabel: "Textiles",
        sharePercent: 24,
        tradeVolumeUsdBillions: 76.9,
        activeSuppliersCount: 460,
        colorHex: "#e63946",
        iconName: "Layers",
      },
      {
        id: "cat-cleantech",
        name: "Solar, PV Cells & Clean Tech",
        shortLabel: "CleanTech",
        sharePercent: 18,
        tradeVolumeUsdBillions: 57.7,
        activeSuppliersCount: 280,
        colorHex: "#2a9d8f",
        iconName: "Sun",
      },
      {
        id: "cat-chemicals",
        name: "Specialty Chemicals & Polymers",
        shortLabel: "Chemicals",
        sharePercent: 14,
        tradeVolumeUsdBillions: 44.8,
        activeSuppliersCount: 220,
        colorHex: "#f4a261",
        iconName: "FlaskConical",
      },
      {
        id: "cat-agri",
        name: "Agri-Commodities & Packaging",
        shortLabel: "Agri & Packs",
        sharePercent: 10,
        tradeVolumeUsdBillions: 31.8,
        activeSuppliersCount: 180,
        colorHex: "#457b9d",
        iconName: "Package",
      },
    ],
  },
}

// ----------------------------------------------------------------------------
// 5. India Manufacturing Hub Clusters Mock Response
// ----------------------------------------------------------------------------
export const MOCK_INDIA_HUBS: ApiResponse<IndiaHubsData> = {
  status: "success",
  timestamp: "2026-10-07T10:00:00Z",
  apiVersion: "v1.2",
  data: {
    totalManufacturingClusters: 7,
    coveredStatesCount: 6,
    clusters: [
      {
        id: "delhi-ncr",
        hubName: "Delhi NCR Cluster",
        stateName: "Delhi / Haryana / UP",
        sectorSpecialization: "Textiles, CleanTech & Packaging",
        verifiedFactoryCount: 176,
        exportOutputUsdMillions: 480,
        demandLevel: "Very High",
        keyProductsProduced: ["Readymade Garments", "Paper Packaging", "Electronic Assemblies"],
        coordinates: [28.7041, 77.1025],
      },
      {
        id: "surat",
        hubName: "Surat Industrial Belt",
        stateName: "Gujarat",
        sectorSpecialization: "Synthetics, Solar Cells & Diamonds",
        verifiedFactoryCount: 142,
        exportOutputUsdMillions: 620,
        demandLevel: "Very High",
        keyProductsProduced: ["Man-made Fabric", "PV Solar Modules", "Precision Polished Jewels"],
        coordinates: [21.1702, 72.8311],
      },
      {
        id: "pune",
        hubName: "Pune Industrial Hub",
        stateName: "Maharashtra",
        sectorSpecialization: "Auto Components & Precision Forging",
        verifiedFactoryCount: 43,
        exportOutputUsdMillions: 310,
        demandLevel: "High",
        keyProductsProduced: ["CNC Machined Shafts", "Die Cast Castings", "Automotive Wiring"],
        coordinates: [18.5204, 73.8567],
      },
      {
        id: "tirupur",
        hubName: "Tirupur Knitwear Cluster",
        stateName: "Tamil Nadu",
        sectorSpecialization: "Cotton Knitwear & Export Apparel",
        verifiedFactoryCount: 209,
        exportOutputUsdMillions: 740,
        demandLevel: "Very High",
        keyProductsProduced: ["100% Combed Cotton Knits", "Organic Tees", "Sportswear Mesh"],
        coordinates: [11.1085, 77.3411],
      },
      {
        id: "kolkata",
        hubName: "Kolkata & Howrah Belt",
        stateName: "West Bengal",
        sectorSpecialization: "Jute Bags, Foundry & Heavy Metals",
        verifiedFactoryCount: 136,
        exportOutputUsdMillions: 290,
        demandLevel: "High",
        keyProductsProduced: ["Hydrocarbon-Free Jute Bags", "Cast Iron Pipe Fittings", "Wire Ropes"],
        coordinates: [22.5726, 88.3639],
      },
      {
        id: "ludhiana",
        hubName: "Ludhiana Engineering Hub",
        stateName: "Punjab",
        sectorSpecialization: "Fasteners, Woolens & Cycle Parts",
        verifiedFactoryCount: 94,
        exportOutputUsdMillions: 210,
        demandLevel: "Moderate",
        keyProductsProduced: ["High-Tensile Fasteners", "Thermal Woolens", "Forged Hand Tools"],
        coordinates: [30.9010, 75.8573],
      },
      {
        id: "morbi",
        hubName: "Morbi Ceramic Hub",
        stateName: "Gujarat",
        sectorSpecialization: "Vitrified Tiles & Sanitaryware",
        verifiedFactoryCount: 118,
        exportOutputUsdMillions: 530,
        demandLevel: "High",
        keyProductsProduced: ["Large Format Porcelain Slabs", "Sanitary Ceramic", "Glazed Wall Tiles"],
        coordinates: [22.8120, 70.8384],
      },
    ],
  },
}

// ----------------------------------------------------------------------------
// 6. Trending Products Radar Mock Response
// ----------------------------------------------------------------------------
export const MOCK_TRENDING_PRODUCTS: ApiResponse<TrendingProductsData> = {
  status: "success",
  timestamp: "2026-10-07T10:00:00Z",
  apiVersion: "v1.2",
  data: {
    trendingItems: [
      {
        id: "trend-1",
        productName: "Recycled Polyester Staple Fiber",
        category: "Sustainable Textiles",
        demandGrowthRate: 38.4,
        indicativeFobPriceUsd: "$1.15 - $1.40 / kg",
        verifiedSuppliersCount: 28,
        averageMatchSpeedMins: 3.5,
        originHub: "Surat, Gujarat",
        hsnCode: "550320",
      },
      {
        id: "trend-2",
        productName: "Solar Monocrystalline PERC Cells (550W)",
        category: "Renewable Energy",
        demandGrowthRate: 44.2,
        indicativeFobPriceUsd: "$0.11 - $0.14 / W",
        verifiedSuppliersCount: 19,
        averageMatchSpeedMins: 4.1,
        originHub: "Ahmedabad, Gujarat",
        hsnCode: "854143",
      },
      {
        id: "trend-3",
        productName: "Precision CNC Stainless Steel Flanges",
        category: "Industrial Engineering",
        demandGrowthRate: 29.1,
        indicativeFobPriceUsd: "$18.50 - $42.00 / pc",
        verifiedSuppliersCount: 42,
        averageMatchSpeedMins: 2.8,
        originHub: "Pune, Maharashtra",
        hsnCode: "730721",
      },
      {
        id: "trend-4",
        productName: "Biodegradable Jute Carrier Bags (Custom Print)",
        category: "Eco Packaging",
        demandGrowthRate: 33.7,
        indicativeFobPriceUsd: "$0.45 - $0.85 / unit",
        verifiedSuppliersCount: 56,
        averageMatchSpeedMins: 2.2,
        originHub: "Kolkata, WB",
        hsnCode: "630510",
      },
      {
        id: "trend-5",
        productName: "High-Tensile Structural Fasteners Grade 8.8",
        category: "Hardware & Construction",
        demandGrowthRate: 26.5,
        indicativeFobPriceUsd: "$1,120 / Tonne",
        verifiedSuppliersCount: 31,
        averageMatchSpeedMins: 5.0,
        originHub: "Ludhiana, Punjab",
        hsnCode: "731815",
      },
    ],
  },
}

// ----------------------------------------------------------------------------
// Async API Simulator Functions (Ready for real API hookup)
// ----------------------------------------------------------------------------
export const tradeIntelligenceApi = {
  getMarketOverview: async (): Promise<ApiResponse<MarketOverviewData>> => {
    // In production: const res = await axios.get('/api/v1/intelligence/overview')
    return MOCK_MARKET_OVERVIEW
  },
  getTopExports: async (): Promise<ApiResponse<TopExportProductsData>> => {
    return MOCK_TOP_EXPORTS
  },
  getTradeCorridors: async (): Promise<ApiResponse<GlobalTradeCorridorsData>> => {
    return MOCK_TRADE_CORRIDORS
  },
  getCategoryDistribution: async (): Promise<ApiResponse<CategoryDistributionData>> => {
    return MOCK_CATEGORY_DISTRIBUTION
  },
  getIndiaHubs: async (): Promise<ApiResponse<IndiaHubsData>> => {
    return MOCK_INDIA_HUBS
  },
  getTrendingProducts: async (): Promise<ApiResponse<TrendingProductsData>> => {
    return MOCK_TRENDING_PRODUCTS
  },
}
